import {
  AspectRatio,
  Box,
  Button,
  HStack,
  IconButton,
  Image,
  Input,
  Link,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Stack,
  Text,
  VStack,
  useBreakpointValue,
  useNumberInput,
  useToast,
} from "@chakra-ui/react";
import {
  GatsbyImage,
  IGatsbyImageData,
  StaticImage,
  getImage,
} from "gatsby-plugin-image";
import {
  HiPlus,
  IoMdRemove,
  MdDeleteOutline,
  MdShoppingCart,
} from "../../customIcons";
import React, { FC, useEffect, useState } from "react";

import ADD_TO_CART from "../../types/AddToCard";
import CrossSellsSection from "../../organisms/CrossSellsSection";
import placeholderImg from "../../../images/productplaceholder.png";
import { useAppState } from "../../context";
import { useMutation } from "@apollo/client";

type ShoppingCartItemType = {
  isGiftWrapping?: boolean;
  slug: string;
  name?: string;
  quantity: number;
  price: string | any;
  imageUrl?: string;
  activeSubstance?: any;
  onChangeQuantity: (qty: number) => void;
  onClickGiftWrapping?: () => void;
  onClickDelete?: (key: any) => void;
  isDrawer?: boolean;
  gratis?: number;
  isSuggsted?: boolean;
  productId?: number;
  updateLoading?: boolean;
  altText: string;
  bundleTitle?: string;
  bundleDescription?: string;
  bundles?: any;
  allItems?: any[];
  imageData?: IGatsbyImageData;
  salePrice?: string;
  variationName?: string;
  maxQuantity?: number; // Add this line
};

const ShoppingCartItem: FC<ShoppingCartItemType> = ({
  slug,
  name,
  quantity,
  imageUrl,
  price,
  activeSubstance,
  onChangeQuantity,
  onClickDelete,
  isDrawer,
  gratis,
  isSuggsted,
  productId,
  updateLoading,
  altText,
  bundleTitle,
  bundleDescription,
  bundles,
  imageData,
  salePrice,
  variationName,
  maxQuantity, // Add this line
}) => {
  const [qtyValue, setQtyValue] = useState(quantity ?? 1);
  const isDesktop = useBreakpointValue({ base: false, sm: true });

  useEffect(() => {
    setQtyValue(quantity);
  }, [quantity]);

  const { getInputProps, getIncrementButtonProps, getDecrementButtonProps } =
    useNumberInput({
      step: 1,
      defaultValue: quantity ?? 1,
      min: 1,
      precision: 0,
      value: qtyValue,
      onChange: (str, num) => setQtyValue(num),
    });

  function multiplyPriceAndQuantity(price: any, quantity: any) {
    const parsedPrice = parseFloat(
      typeof price === "number"
        ? price
        : price?.replace("$", "").replace(",", ""),
    );

    if (!isNaN(parsedPrice)) {
      const totalPrice = parsedPrice * quantity;
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      }).format(totalPrice);
    } else {
      return null;
    }
  }

  const inc = getIncrementButtonProps();
  const dec = getDecrementButtonProps();
  const input = getInputProps();

  const formatPrice = (price: string | undefined): string => {
    if (!price) return "";
    const numericPrice = parseFloat(price.replace("$", "").replace(",", ""));
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(numericPrice);
  };

  const variation = (() => {
    const parts = variationName?.split(" - ");
    return parts?.slice(1).join(" - ").trim();
  })();

  const toast = useToast();
  const { setCart } = useAppState();

  const [addToCart, { loading }] = useMutation(ADD_TO_CART, {
    onCompleted: ({ addToCart }) => {
      toast({
        title: <Text>Added to cart</Text>,
        status: "success",
      });
      setCart(addToCart.cart);

      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: "add_to_cart",
        ecommerce: {
          items: [
            {
              item_name: name,
              item_id: productId,
              price: price ? parseFloat(price.replace("$", "")) : undefined,
              quantity: "1",
            },
          ],
        },
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "There was an error adding your product",
        status: "error",
      });
    },
  });

  const handleAddToCart = () => {
    addToCart({
      variables: {
        input: {
          productId,
          quantity: Number(input.value),
        },
      },
    });
  };

  const image = imageData ? getImage(imageData) : null;

  return (
    <VStack w="100%" maxW="680px" spacing={3}>
      <VStack
        maxW="680px"
        w="100%"
        boxShadow=" 0px 1px 2px 0px rgba(0, 0, 0, 0.05)"
        border="1px solid #E2E8F0"
        borderRadius="8px"
        h={isDrawer ? "240px" : { base: "150px", sm: "180px" }}
        bg="#fff"
        mx={{ base: "auto", xl: "unset" }}
        minW="335px"
        position="relative"
      >
        <HStack h="100%" w="100%">
          {isSuggsted ? (
            <Stack justifyContent="center" w="180px" h="100%" mx="auto">
              <Link href={"/product/" + slug} justifyContent="center">
                <AspectRatio ratio={1 / 1}>
                  {image ? (
                    <GatsbyImage
                      image={image}
                      alt={altText || "Product image"}
                      style={{
                        height: "120px",
                        width: "120px",
                      }}
                    />
                  ) : (
                    <StaticImage
                      rel="preload"
                      src="../../../images/productplaceholder.png"
                      alt="Placeholder"
                      style={{
                        height: "120px",
                        width: "120px",
                        backgroundColor: "transparent !important",
                      }}
                    />
                  )}
                </AspectRatio>
              </Link>
            </Stack>
          ) : (
            <Link href={"/product/" + slug} justifyContent="center">
              <Image
                objectFit="contain"
                borderRadius="8px"
                src={imageUrl ? imageUrl : placeholderImg}
                width={{ base: "84px", sm: "180px" }}
                height={{ base: "84px", sm: "180px" }}
                minW={isDrawer ? "170px" : "80px"}
                minH={isDrawer ? "170px" : "80px"}
                alt={altText}
              />
            </Link>
          )}
          <HStack
            width="100%"
            justifyContent="space-between"
            gap={0}
            h="100%"
            alignItems="flex-start"
          >
            <HStack w="100%" justifyContent="center">
              <VStack gap={0} alignItems="flex-start" w="100%" h="100%">
                <VStack
                  w="100%"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  gap={0}
                >
                  <HStack h="100%" w="100%">
                    <VStack
                      alignItems="flex-start"
                      justifyContent="flex-start"
                      position="relative"
                      w="100%"
                      gap={0}
                    >
                      <Link href={"/product/" + slug}>
                        <Text
                          width={"180px"}
                          pr="8px"
                          mt={4}
                          fontSize={{ base: "16px", sm: "20px" }}
                          fontWeight={isDrawer ? "500" : "600"}
                          lineHeight="120%"
                        >
                          {name?.slice(0, 16) + "..."}
                        </Text>
                        {variationName && (
                          <Text
                            color="#BB5D82"
                            fontWeight="600"
                            marginRight="2"
                            fontSize={{ base: "xs", md: "md" }}
                          >
                            {variation}
                          </Text>
                        )}
                      </Link>

                      <Box noOfLines={3}>
                        {activeSubstance?.map((tag: any, index: any) => {
                          return (
                            <Text
                              key={index}
                              w="100%"
                              color="#4A5568"
                              fontSize="12px"
                            >
                              {tag.name}
                            </Text>
                          );
                        })}
                      </Box>
                    </VStack>
                    {isSuggsted ? (
                      <></>
                    ) : (
                      <IconButton
                        aria-label="remove-item"
                        icon={<MdDeleteOutline width="24px" height="24px" />}
                        onClick={onClickDelete}
                        color="#F56666"
                        w="24px"
                        height="24px"
                        bg="transparent"
                        _hover={{ bg: "transparent" }}
                        isDisabled={updateLoading}
                        position="absolute"
                        right={1}
                        top={4}
                      />
                    )}
                  </HStack>
                  <VStack gap={0}>
                    {gratis && gratis > 0 && (
                      <Text
                        fontSize={isDrawer ? "18px" : "14px"}
                        color={isDrawer ? "#171923" : "#4A5568"}
                        fontWeight={isDrawer ? "600" : "400"}
                      >
                        +1 discounted
                      </Text>
                    )}
                    <Text
                      fontSize={isDrawer ? "18px" : "14px"}
                      color={isDrawer ? "#171923" : "#4A5568"}
                      fontWeight={isDrawer ? "600" : "400"}
                    ></Text>
                  </VStack>
                  {salePrice ? (
                    <HStack pb="8px">
                      <Text
                        fontSize="lg"
                        fontWeight="600"
                        _hover={{ textDecoation: "none" }}
                        color="#39A26A"
                      >
                        {salePrice}
                      </Text>
                      <Text
                        fontSize="14px"
                        color="#4A5568"
                        textDecor="line-through"
                      >
                        {formatPrice(Number(price).toFixed(2))}
                      </Text>
                    </HStack>
                  ) : (
                    <Text width="100px" fontSize="20px" fontWeight="600">
                      {formatPrice(Number(price).toFixed(2))}
                    </Text>
                  )}
                </VStack>

                <HStack position="absolute" bottom={{ base: 1, md: 3 }}>
                  <VStack alignItems="flex-start" gap={0}>
                    <HStack
                      width={{ base: "90px", lg: "110px" }}
                      gap="0"
                      border="1px solid #CBD5E0"
                      borderRadius="6px"
                      h="40px"
                      px={{ base: "2px", lg: "0px" }}
                      pointerEvents={updateLoading ? "none" : "auto"}
                    >
                      {/* <IconButton
                        aria-label="Decrement"
                        icon={<IoMdRemove />}
                        minW="16px"
                        width="16px"
                        bg="transparent"
                        color="#000"
                        {...dec}
                        onClick={() => {
                          onChangeQuantity(parseInt(input.value));
                        }}
                        _hover={{ bg: "transparent" }}
                      /> */}

                      <NumberInput
                        min={1}
                        max={maxQuantity}
                        keepWithinRange
                        value={qtyValue}
                        clampValueOnBlur={true}
                        isDisabled={updateLoading}
                        onChange={(val: any) => {
                          if (!updateLoading) {
                            setQtyValue(val);
                            onChangeQuantity(parseInt(val));
                          }
                        }}
                        focusBorderColor="transparent"
                        border="none"
                        textAlign="center"
                        color="#000"
                        opacity={updateLoading ? 0.4 : 1}
                      >
                        <NumberInputField
                          border="none"
                          w={"100%"}
                          //@ts-ignore
                          disabled={qtyValue >= maxQuantity}
                        />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </HStack>
                  </VStack>

                  {!isDrawer && (
                    <VStack
                      alignItems="flex-start"
                      gap={0}
                      pl={{ base: "10px", lg: "32px" }}
                    >
                      <Text fontSize="14px" color="#A0AEC0">
                        Total
                      </Text>
                      <Text
                        color="#171923"
                        fontSize={{ base: "16px", sm: "20px" }}
                        fontWeight="600"
                      >
                        {multiplyPriceAndQuantity(
                          salePrice ? salePrice : price,
                          quantity,
                        )}
                      </Text>
                    </VStack>
                  )}
                </HStack>
              </VStack>
            </HStack>
            <Box
              mb={4}
              position={{ base: "absolute" }}
              right={{ base: 2 }}
              top={2}
            >
              {isSuggsted && (
                <Button
                  borderRadius="4px"
                  backgroundColor="#3B2060"
                  color="#fff"
                  w={{ base: "100%", md: "140px" }}
                  _hover={{ background: "#6A3AAB" }}
                  fontSize="md"
                  fontWeight="600"
                  leftIcon={<MdShoppingCart fill="#fff" w="18px" h="18px" />}
                  isLoading={loading}
                  pr="8px"
                  loadingText={isDesktop ? "Add to cart" : <MdShoppingCart />}
                  onClick={() => {
                    handleAddToCart();
                  }}
                >
                  {isDesktop ? "Add to cart" : ""}
                </Button>
              )}
            </Box>
          </HStack>
        </HStack>
      </VStack>
      {!isDrawer && !isSuggsted && bundles && (
        <CrossSellsSection
          bundles={bundles}
          title={bundleTitle}
          description={bundleDescription}
          productPrice={price}
          productId={productId}
          productName={name}
          isCart
        />
      )}
    </VStack>
  );
};

export default ShoppingCartItem;
